import type { SandboxLanguage } from "./code-sandbox.step";
import { DEFAULT_CODE } from "./code-sandbox.step";

const KEY_PATTERN = /^[a-z][a-z0-9_]*$/;

interface KeyDef {
  readonly key: string;
  readonly type?: string;
}

const TS_TYPE_MAP: Record<string, string> = {
  string: "string",
  number: "number",
  boolean: "boolean",
  json: "Record<string, unknown>",
  any: "unknown",
};

function tsType(type: string | undefined): string {
  return TS_TYPE_MAP[type ?? "any"] ?? "unknown";
}

function normalize(code: string): string {
  return code
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .trim();
}

export function generateCodeTemplate(
  language: SandboxLanguage,
  inputKeys: ReadonlyArray<KeyDef>,
  outputKeys: ReadonlyArray<KeyDef>,
): string {
  const validInputs = inputKeys.filter((k) => KEY_PATTERN.test(k.key));
  const validOutputs = outputKeys.filter((k) => KEY_PATTERN.test(k.key));

  if (validInputs.length === 0 && validOutputs.length === 0) {
    return DEFAULT_CODE[language];
  }

  if (language === "python") {
    return generatePythonTemplate(validInputs, validOutputs);
  }
  return generateJsTsTemplate(language, validInputs, validOutputs);
}

function generateJsTsTemplate(
  language: "typescript" | "javascript",
  inputs: KeyDef[],
  outputs: KeyDef[],
): string {
  let sig: string;
  if (language === "typescript") {
    const inputType =
      inputs.length > 0
        ? `{ ${inputs.map((k) => `${k.key}: ${tsType(k.type)}`).join("; ")} }`
        : "Record<string, unknown>";
    const outputType =
      outputs.length > 0
        ? `{ ${outputs.map((k) => `${k.key}: ${tsType(k.type)}`).join("; ")} }`
        : "Record<string, unknown>";
    sig = `function handler(input: ${inputType}): ${outputType}`;
  } else {
    sig = "function handler(input)";
  }

  const lines: string[] = [`${sig} {`];

  if (inputs.length > 0) {
    lines.push(`  const { ${inputs.map((k) => k.key).join(", ")} } = input;`);
  }

  if (inputs.length > 0 && outputs.length > 0) {
    lines.push("");
  }

  if (outputs.length === 0) {
    lines.push("  return {};");
  } else {
    lines.push("  return {");
    for (const k of outputs) {
      lines.push(`    ${k.key}: undefined,`);
    }
    lines.push("  };");
  }

  lines.push("}");
  return lines.join("\n");
}

function generatePythonTemplate(inputs: KeyDef[], outputs: KeyDef[]): string {
  const lines: string[] = ["def handler(input):"];

  for (const k of inputs) {
    lines.push(`    ${k.key} = input["${k.key}"]`);
  }

  if (inputs.length > 0 && outputs.length > 0) {
    lines.push("");
  }

  if (outputs.length === 0) {
    lines.push("    return {}");
  } else {
    lines.push("    return {");
    for (const k of outputs) {
      lines.push(`        "${k.key}": None,`);
    }
    lines.push("    }");
  }

  return lines.join("\n");
}

export function isGeneratedTemplate(
  code: string,
  language: SandboxLanguage,
  inputKeys: ReadonlyArray<KeyDef>,
  outputKeys: ReadonlyArray<KeyDef>,
): boolean {
  const normalizedCode = normalize(code);

  if (normalizedCode === normalize(DEFAULT_CODE[language])) {
    return true;
  }

  const generated = generateCodeTemplate(language, inputKeys, outputKeys);
  return normalizedCode === normalize(generated);
}

function extractReturnBlock(code: string): string | null {
  const returnIdx = code.lastIndexOf("return {");
  if (returnIdx === -1) return null;

  let braceStart = code.indexOf("{", returnIdx);
  if (braceStart === -1) return null;

  let depth = 0;
  for (let i = braceStart; i < code.length; i++) {
    if (code[i] === "{") depth++;
    else if (code[i] === "}") {
      depth--;
      if (depth === 0) {
        return code.slice(braceStart + 1, i);
      }
    }
  }
  return null;
}

export function inferOutputKeysFromCode(
  code: string,
  language: SandboxLanguage,
): Array<{ key: string; type: "any" }> {
  const block = extractReturnBlock(code);
  if (!block) return [];

  const keys = new Set<string>();

  if (language === "python") {
    const quoted = /["']([^"']+)["']\s*:/g;
    let match: RegExpExecArray | null;
    while ((match = quoted.exec(block)) !== null) {
      keys.add(match[1]);
    }
  } else {
    const unquoted = /^\s*([a-zA-Z_$][a-zA-Z0-9_$]*)\s*[,:]/gm;
    let match: RegExpExecArray | null;
    while ((match = unquoted.exec(block)) !== null) {
      keys.add(match[1]);
    }

    const quoted = /["']([^"']+)["']\s*:/g;
    while ((match = quoted.exec(block)) !== null) {
      keys.add(match[1]);
    }
  }

  return [...keys]
    .filter((k) => KEY_PATTERN.test(k))
    .map((key) => ({ key, type: "any" as const }));
}
